// Shared status/lifecycle vocabulary for <StatusPill/>. One place for the state -> { label, tone,
// icon } decision that was previously duplicated per page. `tone` is one of the StatusPill tones
// (success | warning | danger | info | neutral). Callers can always override label/tone/icon
// explicitly.
//
// `icon` is the glyph a pill shows when it opts in with `icon="auto"`; pills default to no icon, so
// adding one here never changes an existing call site. Keep them here rather than at the call site
// for the same reason the labels live here: one state should look the same everywhere it appears.

import {
    faCircleCheck, faCircleDot, faCircleXmark, faCircleExclamation, faCircleMinus,
    faTriangleExclamation, faCircleInfo, faCirclePause, faClock, faPencil, faBan,
    faBoxArchive, faRotate, faHourglassHalf, faHourglassEnd, faLinkSlash, faCalendarDay,
    faToggleOn, faCheck,
} from '@fortawesome/free-solid-svg-icons';

const TONES = {
    // active / good
    ACTIVE:     { label: 'Active',     tone: 'success', icon: faCircleCheck },
    LIVE:       { label: 'Live',       tone: 'success', icon: faCircleDot },
    AVAILABLE:  { label: 'Available',  tone: 'success', icon: faCircleCheck },
    ENABLED:    { label: 'Enabled',    tone: 'success', icon: faToggleOn },
    APPROVED:   { label: 'Approved',   tone: 'success', icon: faCircleCheck },
    INCLUDED:   { label: 'Included',   tone: 'success', icon: faCheck },

    // in-progress / upcoming
    SCHEDULED:  { label: 'Scheduled',  tone: 'info',    icon: faCalendarDay },
    RUNNING:    { label: 'Running',    tone: 'info',    icon: faRotate },
    IN_PROGRESS:{ label: 'In progress',tone: 'info',    icon: faHourglassHalf },

    // pending / provisional
    DRAFT:      { label: 'Draft',      tone: 'warning', icon: faPencil },
    AUTOSAVED:  { label: 'Draft',      tone: 'warning', icon: faPencil },
    PAUSED:     { label: 'Paused',     tone: 'warning', icon: faCirclePause },
    // Recoverable - an owner reactivates it - so it reads like Paused, not like the terminal Sunset / Archived
    // it used to share the danger tone with. Matches the suspended lock card (RequireActiveScope).
    SUSPENDED:  { label: 'Suspended',  tone: 'warning', icon: faCirclePause },
    PENDING:    { label: 'Pending',    tone: 'warning', icon: faClock },

    // terminal / neutral
    COMPLETED:  { label: 'Completed',  tone: 'neutral', icon: faCircleCheck },
    'NO-SYNC':  { label: 'No sync',    tone: 'neutral', icon: faLinkSlash },
    NO_SYNC:    { label: 'No sync',    tone: 'neutral', icon: faLinkSlash },

    // off / bad
    INACTIVE:   { label: 'Inactive',   tone: 'danger',  icon: faCircleMinus },
    DISABLED:   { label: 'Disabled',   tone: 'danger',  icon: faBan },
    ARCHIVED:   { label: 'Archived',   tone: 'danger',  icon: faBoxArchive },
    DEPRECATED: { label: 'Deprecated', tone: 'danger',  icon: faTriangleExclamation },
    SUNSET:     { label: 'Sunset',     tone: 'danger',  icon: faHourglassEnd },
    REJECTED:   { label: 'Rejected',   tone: 'danger',  icon: faCircleXmark },
    DECLINED:   { label: 'Declined',   tone: 'danger',  icon: faCircleXmark },
    FAILED:     { label: 'Failed',     tone: 'danger',  icon: faCircleExclamation },
    EXCLUDED:   { label: 'Excluded',   tone: 'danger',  icon: faCircleMinus },
};

/* Per-tone fallback, used by `icon="auto"` when the status is unmapped (or when the pill was given
   a tone and a plain label instead of a status). Neutral takes a hueless dot rather than an
   info/minus glyph, either of which would read as a meaning the neutral tone does not carry. */
export const TONE_ICONS = {
    success: faCircleCheck,
    warning: faTriangleExclamation,
    danger:  faCircleXmark,
    info:    faCircleInfo,
    neutral: faCircleDot,
};

// Title-case an unknown enum value as a readable fallback label ("SOME_STATE" -> "Some state").
const titleCase = (s) => {
    const t = String(s).replace(/[_-]+/g, ' ').trim().toLowerCase();
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
};

// Resolve a raw state/status value to { label, tone, icon? }. Unknown values fall back to a neutral
// pill with a title-cased label so a raw enum is never shown and an unmapped state is never a hard
// error; they carry no icon, so `icon="auto"` falls through to TONE_ICONS for them.
export const toneFor = (state) => {
    if (state == null || state === '') return null;
    const key = String(state).toUpperCase().replace(/\s+/g, '_');
    return TONES[key] || { label: titleCase(state), tone: 'neutral' };
};

export default TONES;
